import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Button } from "@components/ui/Button"
import { Chip } from "@components/ui/Chip"
import { useSiteTheme } from "@features/header/header-theme"
import { TrackingHelpers } from "@features/tracking/tracking-helpers"
import { cn, tv } from "@utils/tw"
import { defineBlock, EditableText, useInlineEditableValue } from "eddev/blocks"
import { Link } from "eddev/routing"
import { ReactNode } from "react"

export const meta: BlockMeta = {
  title: "Mega Menu CTA",
  lazyLoad: false,
  blockStyles: [
    {
      name: "default",
      label: "Default",
      isDefault: true,
    },
    {
      name: "hero",
      label: "Hero",
    },
  ],
}

export default defineBlock("parts/megamenu-cta", (props) => {
  const styles = createStyles({
    variant: props.blockStyle ?? "default",
  })

  let info: ReactNode = null
  let image: ReactNode = null
  let link = ""

  const theme = useSiteTheme().header

  let [panelTitle] = useInlineEditableValue<string>("title")

  if (props.whatToShow === "film") {
    panelTitle = props.film?.title!
    const film = props.film
    if (!film) return null
    const meta: string[] = []
    if (film.credits?.director?.length) {
      meta.push(`Directed by ${film.credits.director.join(", ")}`)
    }
    if (film.filmInfo?.year) {
      meta.push(film.filmInfo.year)
    }

    link = film.uri!
    image = <div className={styles.image()}>{film.media?.thumb && <ResponsiveImage {...film.media?.thumb} max />}</div>
    info = (
      <>
        <div className={styles.info()}>
          <div className={styles.title()}>{film.title}</div>
          <div className={styles.tags()}>
            {meta.map((tag, i) => (
              <Chip key={i} shape="rounded" variant="mid">
                {tag}
              </Chip>
            ))}
          </div>
          <div className={styles.spacer()} />
          <div className={styles.description()}>
            <EditableText store="film_description" placeholder="Enter an optional description" />
          </div>
        </div>
      </>
    )
  } else if (props.whatToShow === "custom") {
    link = props.link?.url!
    info = (
      <>
        <div className={styles.info()}>
          <div className={styles.title()}>
            <EditableText store="title" placeholder="Enter a title" />
          </div>
          <div className={styles.description()}>
            <EditableText store="description" placeholder="Enter an optional description" />
          </div>
          <div className={styles.spacer()} />
          <div className={styles.buttons()}>
            {props.link && (
              <Button shape="branded" variant="primary">
                {props.link.title}
              </Button>
            )}
          </div>
        </div>
      </>
    )
  } else {
    if (env.admin) {
      return <div className={cn(createStyles().base(), "bg-transparent")} />
    } else {
      return null
    }
  }

  if (props.image) {
    image = (
      <div className={styles.image()}>
        <ResponsiveImage {...props.image} max />
      </div>
    )
  }

  return (
    <Link
      href={link}
      className={styles.base({
        // class: theme === "dark" ? "theme-light" : "theme-dark",
        hasImage: !!image,
        variant: props.blockStyle ?? "default",
      })}
      data-mixpanel-area="Desktop Megamenu CTA"
      data-mixpanel-cta-type={props.whatToShow}
      title={panelTitle!}
    >
      {info}
      {image}
    </Link>
  )
})

const createStyles = tv({
  base: "relative bg-bg-mid aspect-[2.2] flex no-underline",
  slots: {
    info: "min-h-max flex flex-col gap-3 text-pretty p-5",
    title: "type-title-l",
    spacer: "flex-1 min-h-4",
    description: "type-body-m",
    tags: "flex gap-1 flex-wrap",
    image: "absolute left-1/2 top-0 bottom-0 right-0",
    buttons: "",
  },
  variants: {
    variant: {
      default: "is-theme-dark:bg-bg-mid is-theme-light:bg-bg-low text-fg",
      hero: "theme-dark bg-orange text-bg",
    },
    hasImage: {
      true: "pr-[50%]",
    },
  },
})
